import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NJButton, NJFormItem, NJIconButton, NJText } from '@engie-group/fluid-design-system-react';
import { postComment, deleteComment, editComment, updateComment, type Comment, type CommentPosition } from '../bffComments';
import { useCurrentUser } from './useCurrentUser';
import { useCommentIdentity } from './useCommentIdentity';
import { formatCommentDate } from './styles';
import { capturePosition, resolvePosition, type PinPoint } from './anchors';
import { ThreadActions } from './ThreadActions';
import { CommentAvatar, CommentMenu } from './CommentControls';
import { submitOnEnter } from './submitOnEnter';
import './comments.css';

// ── Custom pencil cursor for pin mode ────────────────────────────────────────
// SVG can't use CSS variables — color hardcoded to ENGIE blue #0F69AF.
// White outline ensures visibility on both light and dark backgrounds.
// Hotspot (3, 21) = pencil tip (bottom-left of Material "edit" icon).
const PIN_CURSOR = [
  `url("data:image/svg+xml,`,
  `%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E`,
  `%3Cpath d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z`,
  `M20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34`,
  `c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'`,
  ` stroke='white' stroke-width='1.5' stroke-linejoin='round' fill='%230F69AF'/%3E`,
  `%3C/svg%3E") 3 21, crosshair`,
].join('');

// ── Shared ReplyForm ──────────────────────────────────────────────────────────

export function ReplyForm({ slug, parentCommentId, onDone, compact = false }: {
  slug: string; parentCommentId: string; onDone: () => void; compact?: boolean;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { name: authorName } = useCurrentUser();

  const [expanded, setExpanded] = useState(false);
  useEffect(() => { if (!compact) wrapperRef.current?.querySelector('textarea')?.focus(); }, [compact]);
  useEffect(() => {
    if (compact && expanded) wrapperRef.current?.querySelector('textarea')?.focus();
  }, [compact, expanded]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => postComment(slug, text, { parentCommentId, authorName: authorName ?? undefined }),
    onSuccess: () => {
      setError(null);
      setText('');
      setExpanded(false);
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      onDone();
    },
    onError: () => setError("Échec de l'envoi. Réessaie."),
  });

  const submit = () => { if (text.trim() && text.length <= 2000 && !isPending) mutate(); };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    submitOnEnter(e, submit);
  };

  if (compact) return (
    <div className="studio-comment-composer">
      <CommentAvatar name={authorName ?? '?'} />
      <div className="studio-comment-input">
        <div ref={wrapperRef}>
          <NJFormItem id={`reply-${parentCommentId}`} label="Répondre…" aria-label="Répondre au commentaire"
            isMultiline={expanded} rows={2} value={text} maxLength={2000}
            onFocus={() => setExpanded(true)}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { setText(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown} placeholder="Répondre…" />
        </div>
        <div className="studio-comment-composer-actions">
          <NJIconButton label="Envoyer la réponse" title="Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne" icon="arrow_upward"
            variant="primary" scale="sm" isDisabled={!text.trim() || text.length > 2000 || isPending} onClick={submit} />
        </div>
        {error && <NJText scale="xs" role="alert">{error}</NJText>}
      </div>
    </div>
  );

  return (
    <div style={{
      marginLeft: 16, paddingLeft: 10,
      borderLeft: '2px solid var(--nj-core-color-grey-200)',
      marginTop: 8,
    }}>
      <div ref={wrapperRef}>
      <NJFormItem
        id={`reply-${parentCommentId}`}
        label=""
        isMultiline
        rows={2}
        value={text}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setText(e.target.value); setError(null); }}
        onKeyDown={handleKeyDown}
        placeholder="Répondre… (Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne)"
      />
      </div>
      {error && (
        <NJText scale="xs" style={{ color: 'var(--nj-semantic-color-text-danger-default)', marginTop: 2 }}>
          {error}
        </NJText>
      )}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
        <NJButton label="Annuler" variant="secondary" scale="sm" onClick={onDone} />
        <NJButton
          label={isPending ? '…' : 'Répondre'}
          variant="primary" scale="sm"
          onClick={submit}
          disabled={!text.trim() || text.length > 2000 || isPending}
        />
      </div>
    </div>
  );
}

// ── ReplyItem ─────────────────────────────────────────────────────────────────

function ReplyItem({ comment, slug, currentUserEmail, isAdmin = false }: {
  comment: Comment; slug: string; currentUserEmail: string | undefined; isAdmin?: boolean;
}) {
  const isOwn = isAdmin || (!!currentUserEmail && currentUserEmail === comment.authorEmail);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(comment.text);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  useEffect(() => { if (editMode) wrapperRef.current?.querySelector('textarea')?.focus(); }, [editMode]);

  const { mutate: doDelete } = useMutation({
    mutationFn: () => deleteComment(slug, comment.commentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', slug] }),
    onError: () => setError('Impossible de supprimer.'),
  });
  const { mutate: doEdit, isPending: isEditing } = useMutation({
    mutationFn: () => editComment(slug, comment.commentId, draft),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      setEditMode(false);
    },
    onError: () => setError('Impossible de sauvegarder.'),
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (draft.trim()) doEdit(); }
  };

  return (
    <div className="studio-comment-message">
      <CommentAvatar name={comment.authorName} />
      <div className="studio-comment-author">
        <NJText as="span" scale="sm" className="studio-comment-author-name" title={comment.authorName}>{comment.authorName}</NJText>
        <NJText as="span" scale="xs" variant="secondary" title={formatCommentDate(comment.createdAt)}>
          {new Date(comment.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </NJText>
        {!editMode && <CommentMenu onEdit={isOwn ? () => setEditMode(true) : undefined} onDelete={() => doDelete()} />}
      </div>
      <div className="studio-comment-message-text">
      {error && (
        <NJText scale="xs" style={{ color: 'var(--nj-semantic-color-text-danger-default)', marginTop: 2 }}>
          {error}
        </NJText>
      )}
      {editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
          <div ref={wrapperRef}>
          <NJFormItem
            id={`reply-edit-${comment.commentId}`}
            label=""
            isMultiline
            rows={2}
            value={draft}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setDraft(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            placeholder="Ctrl+Entrée pour sauvegarder"
          />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <NJButton label="Annuler" variant="secondary" scale="sm"
              onClick={() => { setDraft(comment.text); setEditMode(false); }} />
            <NJButton label={isEditing ? '…' : 'Sauver'} variant="primary" scale="sm"
              onClick={() => doEdit()} disabled={!draft.trim() || isEditing} />
          </div>
        </div>
      ) : (
        <NJText scale="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 2 }}>
          {comment.text}
        </NJText>
      )}
      </div>
    </div>
  );
}

// ── PinPopup ──────────────────────────────────────────────────────────────────

function PinPopup({ comment, replies, slug, onClose, point, onMove }: {
  comment: Comment; replies: Comment[]; slug: string; onClose: () => void;
  point: PinPoint; onMove: () => void;
}) {
  const { currentUserEmail, isAdmin } = useCommentIdentity();
  const top = Math.max(48, Math.min(point.y, window.innerHeight - 320));
  const isOwn = isAdmin || (!!currentUserEmail && currentUserEmail === comment.authorEmail);
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(comment.text);
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (editMode) wrapperRef.current?.querySelector('textarea')?.focus(); }, [editMode]);

  const { mutate: doDelete, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteComment(slug, comment.commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      onClose();
    },
    onError: () => setError('Impossible de supprimer.'),
  });
  const { mutate: doEdit, isPending: isEditing } = useMutation({
    mutationFn: () => editComment(slug, comment.commentId, draft),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      setEditMode(false);
    },
    onError: () => setError('Impossible de sauvegarder.'),
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); if (draft.trim()) doEdit(); }
  };

  return (
    <div className="studio-comment-card" data-comment-popover role="dialog" aria-label="Discussion"
      style={{
        position: 'fixed',
        left: Math.max(16, Math.min(point.x + 24, window.innerWidth - 376)),
        top,
        maxHeight: `calc(100vh - ${top + 16}px)`, overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="studio-comment-toolbar">
        <NJText as="span" scale="xs">{comment.resolved ? 'Discussion résolue' : 'Commentaire'}</NJText>
        <div className="studio-comment-actions">
          <CommentMenu thread onMove={onMove} onDelete={() => doDelete()} disabled={isDeleting} />
          <ThreadActions compact comment={comment} slug={slug} onStatusChanged={onClose} />
          <NJIconButton label="Fermer le commentaire" title="Fermer (Échap)" icon="close" variant="tertiary" scale="sm" onClick={onClose} />
        </div>
      </div>

      <div className="studio-comment-body">
      {error && (
        <NJText scale="xs" style={{ color: 'var(--nj-semantic-color-text-danger-default)', marginBottom: 4 }}>
          {error}
        </NJText>
      )}
      <div className="studio-comment-message">
        <CommentAvatar name={comment.authorName} />
        <div className="studio-comment-author">
          <NJText as="span" scale="sm" className="studio-comment-author-name" title={comment.authorName}>
            {comment.authorName}
          </NJText>
          <NJText as="span" scale="xs" variant="secondary" title={formatCommentDate(comment.createdAt)}>
            {new Date(comment.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
          </NJText>
          {isOwn && !editMode && <CommentMenu onEdit={() => setEditMode(true)} onDelete={() => doDelete()} disabled={isDeleting} />}
        </div>
      <div className="studio-comment-message-text">
      {editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div ref={wrapperRef}>
          <NJFormItem
            id={`pin-edit-${comment.commentId}`}
            label=""
            isMultiline
            rows={2}
            value={draft}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setDraft(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            placeholder="Ctrl+Entrée pour sauvegarder"
          />
          </div>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            <NJButton label="Annuler" variant="secondary" scale="sm"
              onClick={() => { setDraft(comment.text); setEditMode(false); }} />
            <NJButton label={isEditing ? '…' : 'Sauver'} variant="primary" scale="sm"
              onClick={() => doEdit()} disabled={!draft.trim() || isEditing} />
          </div>
        </div>
      ) : (
        <NJText scale="sm">
          {comment.text}
        </NJText>
      )}
      </div>
      </div>

      {/* Thread */}
      {replies.map((r) => (
        <ReplyItem key={r.commentId} comment={r} slug={slug} currentUserEmail={currentUserEmail} isAdmin={isAdmin} />
      ))}

      {!editMode && (
        <ReplyForm compact
          slug={slug}
          parentCommentId={comment.commentId}
          onDone={() => {}}
        />
      )}
      </div>
    </div>
  );
}

// ── NewPinForm ────────────────────────────────────────────────────────────────

function NewPinForm({ position, point, slug, onClose, onSaved }: {
  position: CommentPosition; point: PinPoint; slug: string;
  onClose: () => void; onSaved: (commentId: string) => void;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef({ start: 0, end: 0 });
  const queryClient = useQueryClient();
  const { name: authorName } = useCurrentUser();
  const multiline = text.length > 0;

  useLayoutEffect(() => {
    const field = wrapperRef.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
    if (field) {
      field.focus();
      field.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
    }
  }, [multiline]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => postComment(slug, text, { ...position, authorName: authorName ?? undefined }),
    onSuccess: ({ commentId }) => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['comments', slug] });
      onSaved(commentId);
    },
    onError: () => setError("Échec de l'envoi. Réessaie."),
  });

  const submit = () => { if (text.trim() && text.length <= 2000 && !isPending) mutate(); };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    submitOnEnter(e, submit);
  };

  const top = Math.max(48, Math.min(point.y, window.innerHeight - 300));
  return (
    <div className="studio-comment-card" data-comment-popover role="dialog" aria-label="Brouillon d’annotation"
      style={{
        position: 'fixed',
        left: Math.max(16, Math.min(point.x + 24, window.innerWidth - 376)),
        top,
        maxHeight: `calc(100vh - ${top + 16}px)`, overflowY: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="studio-comment-toolbar">
        <NJText as="span" scale="xs">Nouveau commentaire</NJText>
        <NJIconButton label="Annuler l’annotation" icon="close" variant="tertiary" scale="sm" onClick={onClose} />
      </div>
      <div className="studio-comment-body">
      <div ref={wrapperRef}>
      <NJFormItem
        id="pin-new-text"
        label="Ajouter un commentaire…"
        isMultiline={multiline}
        rows={3}
        maxLength={2000}
        aria-label="Nouveau commentaire"
        value={text}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          selectionRef.current = {
            start: e.target.selectionStart ?? e.target.value.length,
            end: e.target.selectionEnd ?? e.target.value.length,
          };
          setText(e.target.value);
          setError(null);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Ajouter un commentaire…"
      />
      </div>
      {error && (
        <NJText scale="xs" style={{ color: 'var(--nj-semantic-color-text-danger-default)', marginTop: 4 }}>
          {error}
        </NJText>
      )}
      <div className="studio-comment-composer-actions">
        <NJIconButton
          label={isPending ? 'Envoi…' : 'Épingler le commentaire'} title="Entrée pour envoyer, Maj+Entrée pour une nouvelle ligne"
          icon="arrow_upward" variant="primary" scale="sm"
          onClick={submit}
          isDisabled={!text.trim() || text.length > 2000 || isPending}
        />
      </div>
      </div>
    </div>
  );
}

// ── PinMarker ─────────────────────────────────────────────────────────────────

function PinMarker({ comment, replies, slug, forceOpen, onOpen, point, contentRef, onMove }: {
  comment: Comment; replies: Comment[]; slug: string;
  forceOpen?: boolean; onOpen?: () => void;
  point: PinPoint; contentRef: RefObject<HTMLElement>; onMove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const isOpen = forceOpen ?? open;
  const [dragPoint, setDragPoint] = useState<PinPoint | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const queryClient = useQueryClient();
  const { mutate: move, isPending } = useMutation({
    mutationFn: (position: CommentPosition) => updateComment(slug, comment.commentId, position),
    onSuccess: () => {
      setMoveError(null);
      void queryClient.invalidateQueries({ queryKey: ['comments', slug] });
    },
    onError: () => setMoveError('Impossible de déplacer le commentaire. Réessaie.'),
    onSettled: () => setDragPoint(null),
  });
  const displayPoint = dragPoint ?? point;

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (forceOpen !== undefined) {
      onOpen?.();
    } else {
      setOpen((v) => !v);
    }
  };

  return (
    <>
      <button
        className="studio-comment-pin"
        onClick={toggle}
        disabled={isPending}
        aria-expanded={isOpen}
        aria-label={`${comment.authorName} : ${comment.text.slice(0, 60)}. Glisser pour déplacer, Entrée pour ouvrir.`}
        title="Glisser pour déplacer · Cliquer pour ouvrir"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.stopPropagation();
          suppressClick.current = false;
          drag.current = { x: e.clientX, y: e.clientY, moved: false };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!drag.current) return;
          if (Math.hypot(e.clientX - drag.current.x, e.clientY - drag.current.y) > 5) drag.current.moved = true;
          if (drag.current.moved) setDragPoint({ x: e.clientX, y: e.clientY });
        }}
        onPointerUp={(e) => {
          const moved = drag.current?.moved;
          drag.current = null;
          if (!moved || !contentRef.current) return;
          suppressClick.current = true;
          try { move(capturePosition(contentRef.current, e.clientX, e.clientY)); }
          catch (error) {
            setDragPoint(null);
            setMoveError(error instanceof Error ? error.message : 'Position invalide.');
          }
        }}
        onPointerCancel={() => { drag.current = null; setDragPoint(null); }}
        style={{
          position: 'fixed',
          left: displayPoint.x,
          top: displayPoint.y,
        }}
      >
        <NJText as="span" scale="xs" aria-hidden="true">{comment.authorName.trim().charAt(0).toUpperCase() || '?'}</NJText>
      </button>
      {moveError && <NJText role="alert" scale="xs" style={{ position: 'fixed', left: point.x, top: point.y, pointerEvents: 'all', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>{moveError}</NJText>}
      {isOpen && !dragPoint && (
        <PinPopup
          comment={comment}
          replies={replies}
          slug={slug}
          point={point}
          onMove={onMove}
          onClose={() => {
            if (forceOpen !== undefined) onOpen?.();
            else setOpen(false);
          }}
        />
      )}
    </>
  );
}

// ── CommentsOverlay ───────────────────────────────────────────────────────────

export function CommentsOverlay({ slug, comments, active, highlightCommentId, onPinOpened, contentRef, relocateId, onRelocated }: {
  slug: string; comments: Comment[]; active: boolean;
  highlightCommentId?: string | null; onPinOpened?: (commentId: string | null) => void;
  contentRef: RefObject<HTMLElement>; relocateId?: string | null; onRelocated: () => void;
}) {
  const [pendingPin, setPendingPin] = useState<CommentPosition | null>(null);
  const [openPinId, setOpenPinId] = useState<string | null>(null);
  const [positions, setPositions] = useState<Record<string, PinPoint>>({});
  const [pendingPoint, setPendingPoint] = useState<PinPoint | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const client = useQueryClient();
  const { mutate: move, isPending: isMoving } = useMutation({
    mutationFn: ({ id, position }: { id: string; position: CommentPosition }) => updateComment(slug, id, position),
    onSuccess: () => {
      setMovingId(null);
      setError(null);
      onRelocated();
      void client.invalidateQueries({ queryKey: ['comments', slug] });
    },
    onError: () => setError('Impossible de déplacer le commentaire. Réessaie.'),
  });
  const rootPins = comments.filter((c) => c.xPct !== undefined && !c.parentCommentId && (!c.resolved || c.commentId === highlightCommentId));

  useEffect(() => {
    if (relocateId) { setMovingId(relocateId); setOpenPinId(null); }
  }, [relocateId]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const next: Record<string, PinPoint> = {};
      comments.forEach((c) => {
        if (c.xPct === undefined || c.parentCommentId) return;
        const point = resolvePosition(root, c);
        if (point) next[c.commentId] = point;
      });
      setPositions(next);
      setPendingPoint(pendingPin ? resolvePosition(root, pendingPin) : null);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    const observer = new MutationObserver((records) => {
      if (records.some((record) => !(record.target instanceof Element && record.target.closest('[data-comment-ui]')))) schedule();
    });
    observer.observe(root, { subtree: true, childList: true, attributes: true });
    const resize = new ResizeObserver(schedule);
    resize.observe(root);
    root.querySelectorAll('[data-comment-anchor], button, input, textarea, canvas').forEach((el) => resize.observe(el));
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    measure();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
    };
  }, [comments, pendingPin, contentRef]);

  useEffect(() => {
    if (!active) setPendingPin(null);
  }, [active]);

  useEffect(() => {
    const selectFocused = (event: KeyboardEvent) => {
      const root = contentRef.current;
      const target = document.activeElement;
      if (event.key !== 'Enter' || (!active && !movingId) || pendingPin || isMoving ||
          !root || !(target instanceof HTMLElement) || !root.contains(target) || target.closest('[data-comment-ui]')) return;
      event.preventDefault();
      event.stopPropagation();
      const rect = target.getBoundingClientRect();
      try {
        const position = capturePosition(root, rect.left + rect.width / 2, rect.top + rect.height / 2);
        setError(null);
        if (movingId) move({ id: movingId, position });
        else { setPendingPin(position); setOpenPinId(null); }
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Position invalide.');
      }
    };
    window.addEventListener('keydown', selectFocused, true);
    return () => window.removeEventListener('keydown', selectFocused, true);
  }, [active, movingId, pendingPin, isMoving, contentRef, move]);

  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // A first Escape only discards the draft; the layout then keeps annotation mode on.
        if (pendingPin) event.preventDefault();
        setPendingPin(null); setOpenPinId(null); setMovingId(null); setError(null); onRelocated(); onPinOpened?.(null);
      }
    };
    window.addEventListener('keydown', escape, true);
    return () => window.removeEventListener('keydown', escape, true);
  }, [pendingPin, onRelocated, onPinOpened]);

  // Sync external highlight → open/close the right pin
  useEffect(() => {
    if (highlightCommentId !== undefined) setOpenPinId(highlightCommentId);
  }, [highlightCommentId]);

  // Clicking outside an open comment window closes it; that same click must not drop a new pin.
  const closedAt = useRef(0);
  useEffect(() => {
    if (!pendingPin && !openPinId) return;
    const closeOnOutside = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[data-comment-popover], .studio-comment-pin')) return;
      closedAt.current = Date.now();
      setPendingPin(null);
      if (openPinId) { setOpenPinId(null); onPinOpened?.(null); }
    };
    document.addEventListener('pointerdown', closeOnOutside, true);
    return () => document.removeEventListener('pointerdown', closeOnOutside, true);
  }, [pendingPin, openPinId, onPinOpened]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (Date.now() - closedAt.current < 1000) { closedAt.current = 0; return; }
    if ((!active && !movingId) || pendingPin || isMoving || !contentRef.current) return;
    try {
      const position = capturePosition(contentRef.current, e.clientX, e.clientY);
      setError(null);
      if (movingId) move({ id: movingId, position });
      else setPendingPin(position);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Position invalide.');
    }
  };

  const handlePinOpen = (commentId: string) => {
    const next = openPinId === commentId ? null : commentId;
    setOpenPinId(next);
    onPinOpened?.(next);
  };

  return (
    <div data-comment-ui style={{ position: 'fixed', inset: 0, zIndex: 999, pointerEvents: 'none' }}>
      {(active || movingId) && !pendingPin && (
        <div
          onClick={handleClick}
          style={{
            position: 'fixed',
            left: contentRef.current?.getBoundingClientRect().left ?? 0,
            top: Math.max(48, contentRef.current?.getBoundingClientRect().top ?? 48),
            right: Math.max(0, window.innerWidth - (contentRef.current?.getBoundingClientRect().right ?? window.innerWidth)),
            bottom: Math.max(0, window.innerHeight - (contentRef.current?.getBoundingClientRect().bottom ?? window.innerHeight)),
            cursor: PIN_CURSOR,
            pointerEvents: 'all',
            background: 'rgba(0,0,0,0.04)',
          }}
        />
      )}


      {error && <NJText role="alert" style={{ position: 'fixed', top: 56, left: 16, background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>{error}</NJText>}
      {movingId && !error && <NJText role="status" style={{ position: 'fixed', top: 56, left: 16, background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>Nouvelle cible : clic ou Tab puis Entrée · Échap pour annuler</NJText>}
      {active && !movingId && !pendingPin && !error && !highlightCommentId && <NJText role="status" scale="sm" style={{ position: 'fixed', top: 56, left: 16, padding: 'var(--nj-semantic-size-spacing-4) var(--nj-semantic-size-spacing-8)', background: 'var(--nj-semantic-color-background-neutral-primary-default)' }}>Mode annotation : clique pour ajouter des commentaires · Échap ou ✏️ pour quitter</NJText>}
      {highlightCommentId && comments.some((c) => c.commentId === highlightCommentId) && !positions[highlightCommentId] && !movingId && (
        <div style={{ position: 'fixed', top: 56, left: 16, pointerEvents: 'all', background: 'var(--nj-semantic-color-background-neutral-primary-default)', padding: 'var(--nj-semantic-size-spacing-16)' }}>
          <NJText scale="sm">Composant hors écran ou absent de cette vue. Retrouve sa vue ou repositionne l’annotation.</NJText>
          <NJButton label="Repositionner" variant="secondary" scale="sm" onClick={() => setMovingId(highlightCommentId)} />
          <NJButton label="Fermer" variant="secondary" scale="sm" onClick={() => onPinOpened?.(null)} />
        </div>
      )}
      {rootPins.filter((c) => positions[c.commentId] && c.commentId !== movingId).map((c) => (
        <PinMarker
          key={c.commentId}
          comment={c}
          replies={comments.filter((r) => r.parentCommentId === c.commentId)}
          slug={slug}
          point={positions[c.commentId]}
          contentRef={contentRef}
          onMove={() => { setMovingId(c.commentId); setOpenPinId(null); }}
          forceOpen={openPinId === c.commentId}
          onOpen={() => handlePinOpen(c.commentId)}
        />
      ))}

      {pendingPin && pendingPoint && active && (
        <>
          <div className="studio-comment-pin" aria-hidden="true" style={{
            position: 'fixed',
            left: pendingPoint.x,
            top: pendingPoint.y,
            opacity: 0.7,
            pointerEvents: 'none',
          }} />
          <NewPinForm
            position={pendingPin}
            point={pendingPoint}
            slug={slug}
            onClose={() => setPendingPin(null)}
            onSaved={(commentId) => { setPendingPin(null); setOpenPinId(commentId); onPinOpened?.(commentId); }}
          />
        </>
      )}
    </div>
  );
}
